import {applyMiddleware, compose, createStore} from 'redux';
import {rootReducer} from './reducers';
import networkMiddleware from './middleware/networkMiddleware';
import modalsMiddleware from './middleware/modalsMiddleware';
import tutorialMiddleware from './middleware/tutorialMiddleware';
import missionMiddleware from './middleware/missionMiddleware';
import {createConstructionPlanMiddleware} from './middleware/constructionPlanMiddleware';
import unitMovementAnimationMiddleware from './middleware/unitMovementAnimationMiddleware';
import meleeAnimationMiddleware from './middleware/meleeAnimationMiddleware';
import cityCaptureAnimationMiddleware from './middleware/cityCaptureAnimationMiddleware';
import ruinsIncomeAnimationMiddleware from './middleware/ruinsIncomeAnimationMiddleware';
import unitDeathAnimationMiddleware from './middleware/unitDeathAnimationMiddleware';
import supplyRecruitAnimationMiddleware from './middleware/supplyRecruitAnimationMiddleware';
import unitStarvationAnimationMiddleware from './middleware/unitStarvationAnimationMiddleware';
import turnInputGuardMiddleware from './middleware/turnInputGuardMiddleware';
import {setTurnInputBlockedListener} from '../utils/gameState/turn/turnInputLock';
import {sendNetworkEvent} from '../analytics/gameAnalytics';
import {createTutorialReplayMiddleware} from './middleware/tutorialReplayMiddleware';
import {createLocalMatchReplayMiddleware} from './middleware/localMatchReplayMiddleware';
import {
  createTutorialReplayRecorder,
  RUN_RECORDER_FLUSH_MODES
} from '../utils/tutorial/replay/createTutorialReplayRecorder';
import {
  getLocalMatchReplayRecorder,
  getTutorialReplayRecorder,
  setLocalMatchReplayRecorder,
  setTutorialReplayRecorder
} from '../utils/tutorial/replay/tutorialReplayRecorderRegistry';
import {localRunTransport} from '../utils/localMatch/replay/localRunTransport';
import {
  subscribeToTutorialReplayPageHide,
  tutorialReplayTransport
} from '../utils/tutorial/replay/tutorialReplayTransport';
import {getNetworkSocket, sendNetworkMessage} from '../utils/networkSocket';
import {REGISTRY_SINGLETON} from '../scenes/Registry';

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

// План строительства в сетевой игре: применяется в начале своего хода только
// при открытом сокете матча — действия уходят на сервер через networkMiddleware
const constructionPlanMiddleware = createConstructionPlanMiddleware({
  isSocketOpen: () => getNetworkSocket()?.readyState === WebSocket.OPEN,
  // При переподключении LobbyPage ставит новый объект сокета: повтор плана —
  // только по LOAD_STATE нового соединения
  getSocket: getNetworkSocket,
  // Снимок, собранный сервером раньше действий плана, затер их у этого
  // клиента — свежее состояние вернет их
  requestState: () => sendNetworkMessage({type: 'REQUEST_STATE'}),
  // Запланированное сняла чужая стройка на той же ячейке — звучит как отмена
  // строительства. Своего звука у применения плана нет: начало хода и так
  // озвучено, а остальным звук стройки дают пришедшие к ним действия
  playPlanCancelledSound: () => {
    REGISTRY_SINGLETON.getSoundManager()?.playUndoClick?.();
  },
  // Ход завершен автоматически сразу после применения плана: оверлея «Ваш
  // ход» нет, но его звук (звук фазы строительства) подтверждает игроку, что
  // запланированное построено
  playTurnStartSound: () => {
    REGISTRY_SINGLETON.getSoundManager()?.playPhaseConstruction?.();
  },
});

// Запись прохождения обучения на сервер по ходу главы (см.
// createTutorialReplayRecorder). Запускается бутстрапом главы в GameContent
setTutorialReplayRecorder(createTutorialReplayRecorder({
  transport: tutorialReplayTransport,
  subscribeToPageHide: subscribeToTutorialReplayPageHide,
}));
const tutorialReplayMiddleware = createTutorialReplayMiddleware({getRecorder: getTutorialReplayRecorder});

// Запись локального матча: тот же движок, но отправка раз в раунд, а не по
// таймеру (см. localMatchReplayMiddleware). Запускается бутстрапом локальной
// партии в GameContent
setLocalMatchReplayRecorder(createTutorialReplayRecorder({
  transport: localRunTransport,
  subscribeToPageHide: subscribeToTutorialReplayPageHide,
  flushMode: RUN_RECORDER_FLUSH_MODES.MANUAL,
}));
const localMatchReplayMiddleware = createLocalMatchReplayMiddleware({getRecorder: getLocalMatchReplayRecorder});

// Попытка действовать под блокировкой ввода — в аналитику (раз на ход): по ней
// видно, сколько действий после нуля таймера блокировка не пустила в закрытый ход
setTurnInputBlockedListener(() => sendNetworkEvent('turn_input_blocked'));

const store = createStore(
  rootReducer,
  composeEnhancers(applyMiddleware(
    // Самым первым — страж блокировки ввода после нуля таймера хода: экшен,
    // который он отбросил, не должен увидеть никто, в том числе анимация
    // перемещения ниже (она добила бы полет отряда ради отброшенного экшена)
    turnInputGuardMiddleware,
    // Дальше: анимация перемещения отряда должна быть добита (и перемещение
    // записано в стор) до того, как экшен увидят остальные middleware
    unitMovementAnimationMiddleware,
    tutorialMiddleware, missionMiddleware,
    // Запись обучения — там же, где сетевая партия отдает действия серверу:
    // после гейта обучения, перед networkMiddleware
    tutorialReplayMiddleware, localMatchReplayMiddleware,
    networkMiddleware, modalsMiddleware, constructionPlanMiddleware,
    // Облет захваченных городов — после modalsMiddleware: его флаг
    // поднимается раньше флага модалки захвата, и модалка не мелькает до
    // облета
    cityCaptureAnimationMiddleware,
    // Рост дохода руин — когда закрылось все, что перед ним в очереди
    ruinsIncomeAnimationMiddleware,
    // Гибель отрядов — по той же причине: итоги боя не мелькают раньше нее
    unitDeathAnimationMiddleware,
    // Новобранцы крысолюдов за снабжение: как и удары ниже, смотрят на найм, дошедший до редьюсера
    supplyRecruitAnimationMiddleware,
    // Гибель от голода: смотрит на конец фазы снабжения, дошедший до редьюсера
    unitStarvationAnimationMiddleware,
    // Последним — анимация ударов ближнего боя: она смотрит на расчет боя,
    // который дошел до редьюсера (экшен, отброшенный гейтами выше, сюда не
    // попадает), и запускается сразу после него
    meleeAnimationMiddleware
  ))
);

// Дев-доступ к стору из консоли браузера (диагностика миссий и обучения)
if (process.env.NODE_ENV === 'development') {
  window.store = store;
}

export default store;
